import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { BarGraphNoData } from './bar-graph-no-data';
import { Button } from '@/components/button';
import { formatCurrency, formatPercent } from '@/utils/money';
import { GraphItemData } from './types';
import { Platform, StyleSheet, View } from 'react-native';
import { PriceGraph } from './price-graph';
import { PriceTrendsDisplayType, PriceTrendsTimePeriod } from '@/enums';
import { useGetPriceTrends } from '@/hooks/use-get-price-trends';
import { useSupplierCurrencyCode } from '@/hooks/use-supplier-currency-code';

interface PriceTrendsDisplayData {
  graphData: GraphItemData[];
  lastValue: number | null;
  medianPrice: number;
}

type PriceTrendsGraphData = {
  [key in PriceTrendsTimePeriod]: {
    [key in PriceTrendsDisplayType]: PriceTrendsDisplayData;
  };
};

interface PriceTrendsGraphProps {
  displayMode: PriceTrendsDisplayType;
  restaurantGroupUUIDs: string[];
  supplierUUID: string;
  variantPackUUID: string;
}

export const PriceTrendsGraph: React.FC<PriceTrendsGraphProps> = ({
  displayMode,
  restaurantGroupUUIDs = [],
  variantPackUUID,
  supplierUUID,
}) => {
  const { priceTrendsData, lastGP, lastPrice, loading } = useGetPriceTrends(
    supplierUUID,
    variantPackUUID,
    restaurantGroupUUIDs,
  );
  const { currencyCode: supplierCurrencyCode } =
    useSupplierCurrencyCode(supplierUUID);

  const [hasSetInitialTimePeriods, setHasSetInitialTimePeriods] =
    useState(false);

  const formatValue = useCallback(
    (value: number) => {
      switch (displayMode) {
        case PriceTrendsDisplayType.Price:
          return formatCurrency(value, supplierCurrencyCode);
        case PriceTrendsDisplayType.GP:
          return formatPercent(Number(value / 100));
        default:
          return String(value);
      }
    },
    [displayMode, supplierCurrencyCode],
  );

  const formatForGraphData = useCallback(
    (occurrences: number, value: number) => {
      return {
        label: formatValue(value),
        x: String(value),
        y: occurrences,
      };
    },
    [formatValue],
  );

  const transformedPriceTrendsData: PriceTrendsGraphData | undefined =
    useMemo(() => {
      if (priceTrendsData) {
        return {
          [PriceTrendsTimePeriod.L7]: {
            [PriceTrendsDisplayType.Price]: {
              graphData: priceTrendsData.price_occurrences_l7.map(
                ({ occurrences, price }) =>
                  formatForGraphData(occurrences, price),
              ),
              lastValue: lastPrice,
              medianPrice: priceTrendsData.median_price_l7,
            },
            [PriceTrendsDisplayType.GP]: {
              graphData: priceTrendsData.gp_occurrences_l7.map(
                ({ occurrences, gross_profit }) =>
                  formatForGraphData(occurrences, gross_profit),
              ),
              lastValue: lastGP,
              medianPrice: priceTrendsData.median_gp_l7,
            },
          },
          [PriceTrendsTimePeriod.L30]: {
            [PriceTrendsDisplayType.Price]: {
              graphData: priceTrendsData.price_occurrences_l30.map(
                ({ occurrences, price }) =>
                  formatForGraphData(occurrences, price),
              ),
              lastValue: lastPrice,
              medianPrice: priceTrendsData.median_price_l30,
            },
            [PriceTrendsDisplayType.GP]: {
              graphData: priceTrendsData.gp_occurrences_l30.map(
                ({ occurrences, gross_profit }) =>
                  formatForGraphData(occurrences, gross_profit),
              ),
              lastValue: lastGP,
              medianPrice: priceTrendsData.median_gp_l30,
            },
          },
          [PriceTrendsTimePeriod.L90]: {
            [PriceTrendsDisplayType.Price]: {
              graphData: priceTrendsData.price_occurrences_l90.map(
                ({ occurrences, price }) =>
                  formatForGraphData(occurrences, price),
              ),
              lastValue: lastPrice,
              medianPrice: priceTrendsData.median_price_l90,
            },
            [PriceTrendsDisplayType.GP]: {
              graphData: priceTrendsData.gp_occurrences_l90.map(
                ({ occurrences, gross_profit }) =>
                  formatForGraphData(occurrences, gross_profit),
              ),
              lastValue: lastGP,
              medianPrice: priceTrendsData.median_gp_l90,
            },
          },
          [PriceTrendsTimePeriod.L365]: {
            [PriceTrendsDisplayType.Price]: {
              graphData: priceTrendsData.price_occurrences_yoy.map(
                ({ occurrences, price }) =>
                  formatForGraphData(occurrences, price),
              ),
              lastValue: lastPrice,
              medianPrice: priceTrendsData.median_price_yoy,
            },
            [PriceTrendsDisplayType.GP]: {
              graphData: priceTrendsData.gp_occurrences_yoy.map(
                ({ occurrences, gross_profit }) =>
                  formatForGraphData(occurrences, gross_profit),
              ),
              lastValue: lastGP,
              medianPrice: priceTrendsData.median_gp_yoy,
            },
          },
        };
      }
    }, [formatForGraphData, lastGP, lastPrice, priceTrendsData]);

  const [timePeriod, setTimePeriod] = useState<PriceTrendsTimePeriod>(
    PriceTrendsTimePeriod.L7,
  );

  const dataEmpty: boolean = useMemo(() => {
    if (transformedPriceTrendsData) {
      return Object.values(transformedPriceTrendsData[timePeriod]).every(
        ({ graphData }) => graphData.length === 0,
      );
    }
    return true;
  }, [timePeriod, transformedPriceTrendsData]);

  useEffect(() => {
    if (hasSetInitialTimePeriods) {
      return;
    }
    // Set the time period to the first available time period with data
    if (transformedPriceTrendsData) {
      if (
        transformedPriceTrendsData[PriceTrendsTimePeriod.L7][
          PriceTrendsDisplayType.Price
        ].graphData.length !== 0
      ) {
        setTimePeriod(PriceTrendsTimePeriod.L7);
      } else if (
        transformedPriceTrendsData[PriceTrendsTimePeriod.L30][
          PriceTrendsDisplayType.Price
        ].graphData.length !== 0
      ) {
        setTimePeriod(PriceTrendsTimePeriod.L30);
      } else if (
        transformedPriceTrendsData[PriceTrendsTimePeriod.L90][
          PriceTrendsDisplayType.Price
        ].graphData.length !== 0
      ) {
        setTimePeriod(PriceTrendsTimePeriod.L90);
      } else if (
        transformedPriceTrendsData[PriceTrendsTimePeriod.L365][
          PriceTrendsDisplayType.Price
        ].graphData.length !== 0
      ) {
        setTimePeriod(PriceTrendsTimePeriod.L365);
      }
      setHasSetInitialTimePeriods(true);
    }
  }, [transformedPriceTrendsData, hasSetInitialTimePeriods]);

  const priceTrendsDataForDisplay = transformedPriceTrendsData
    ? transformedPriceTrendsData[timePeriod][displayMode]
    : null;

  return (
    <View style={styles.container}>
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator />
        </View>
      ) : transformedPriceTrendsData &&
        timePeriod &&
        transformedPriceTrendsData[timePeriod][displayMode].graphData.length >
          0 ? (
        <View style={styles.graphContainer} testID="price-graph-container">
          <PriceGraph
            formatValue={formatValue}
            graphData={priceTrendsDataForDisplay!.graphData}
            lastValue={priceTrendsDataForDisplay!.lastValue}
            median={priceTrendsDataForDisplay!.medianPrice}
          />
        </View>
      ) : (
        <BarGraphNoData empty={dataEmpty} />
      )}
      <View
        style={
          Platform.OS === 'web'
            ? styles.buttonContainerWeb
            : styles.buttonContainer
        }
      >
        <View>
          <Button
            onPress={() => setTimePeriod(PriceTrendsTimePeriod.L7)}
            rounded
            size="xsmall"
            trackingData={{ name: 'price_trends_graph.l7' }}
            type={
              timePeriod === PriceTrendsTimePeriod.L7 ? 'primary' : 'secondary'
            }
          >
            {PriceTrendsTimePeriod.L7}
          </Button>
        </View>
        <View>
          <Button
            onPress={() => setTimePeriod(PriceTrendsTimePeriod.L30)}
            rounded
            size="xsmall"
            trackingData={{ name: 'price_trends_graph.l30' }}
            type={
              timePeriod === PriceTrendsTimePeriod.L30 ? 'primary' : 'secondary'
            }
          >
            {PriceTrendsTimePeriod.L30}
          </Button>
        </View>
        <View>
          <Button
            onPress={() => setTimePeriod(PriceTrendsTimePeriod.L90)}
            rounded
            size="xsmall"
            trackingData={{ name: 'price_trends_graph.l90' }}
            type={
              timePeriod === PriceTrendsTimePeriod.L90 ? 'primary' : 'secondary'
            }
          >
            {PriceTrendsTimePeriod.L90}
          </Button>
        </View>
        <View>
          <Button
            onPress={() => setTimePeriod(PriceTrendsTimePeriod.L365)}
            rounded
            size="xsmall"
            trackingData={{ name: 'price_trends_graph.l365' }}
            type={
              timePeriod === PriceTrendsTimePeriod.L365
                ? 'primary'
                : 'secondary'
            }
          >
            {PriceTrendsTimePeriod.L365}
          </Button>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 16,
    marginTop: 8,
  },
  buttonContainerWeb: {
    flexDirection: 'row',
    gap: 16,
    justifyContent: 'flex-start',
    marginBottom: 16,
    marginTop: 8,
  },
  container: {
    marginTop: 8,
  },
  graphContainer: {
    marginTop: 8,
  },
  loadingContainer: {
    height: 224,
    justifyContent: 'center',
  },
});
